import React, { JSX } from "react";

export default function PrivacyPolicyPage(): JSX.Element {
  return (
    <div className="privacy-policy-page" style={{ padding: "40px", maxWidth: "800px", margin: "0 auto", lineHeight: "1.6" }}>
      <h1>Privacy Policy</h1>
      <p style={{ color: "#666", marginBottom: "32px" }}>Last Updated: February 2, 2026</p>

      <section style={{ marginBottom: "24px" }}>
        <h2>1. Introduction</h2>
        <p>
          This Privacy Policy describes how Gratitude Board ("we," "us," or "our") collects, uses, and protects your information when you use our mobile application and website (collectively, the "App"). We are committed to protecting your privacy and ensuring transparency about our data practices.
        </p>
        <p style={{ marginTop: "12px" }}>
          By using the App, you consent to the collection and use of your information as described in this Privacy Policy. If you do not agree with this policy, please do not use the App.
        </p>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>2. Information We Collect</h2>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>Account Information</h3>
        <p>When you create an account, we collect:</p>
        <ul>
          <li><strong>Email address</strong> (required for account creation)</li>
          <li><strong>Display name</strong> (optional, provided by you or from your sign-in provider)</li>
          <li><strong>Profile photo</strong> (optional, if you choose to upload one)</li>
        </ul>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>Authentication Data</h3>
        <p>Depending on how you sign in, we receive limited information from third-party providers:</p>

        <p><strong>Sign in with Apple:</strong></p>
        <ul>
          <li>Your name (only if you choose to share it)</li>
          <li>Your email address (or an Apple relay email if you choose to hide your email)</li>
          <li>Apple does not share your Apple ID password with us</li>
        </ul>

        <p><strong>Sign in with Google:</strong></p>
        <ul>
          <li>Your name</li>
          <li>Your email address</li>
          <li>Your profile photo (if available)</li>
          <li>Google does not share your Google password with us</li>
        </ul>

        <p><strong>Email and Password:</strong></p>
        <ul>
          <li>Your email address</li>
          <li>Your password (stored securely using industry-standard hashing; we cannot see your password)</li>
        </ul>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>Content You Create</h3>
        <p>All content you create is stored and visible to other board members:</p>
        <ul>
          <li>Gratitude entries (text and images)</li>
          <li>Boards you create or join</li>
          <li>Reactions to other users' entries</li>
        </ul>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>Information We Do NOT Collect</h3>
        <ul>
          <li>We do not collect analytics or usage data</li>
          <li>We do not use crash reporting services</li>
          <li>We do not access your contacts, camera, or other device data</li>
          <li>We do not collect location data</li>
          <li>We do not collect health or fitness data</li>
          <li>We do not use tracking or advertising identifiers</li>
        </ul>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>3. How We Use Your Information</h2>
        <p>We use your information solely to provide the App:</p>
        <ul>
          <li>Create and manage your account</li>
          <li>Display your profile to other board members</li>
          <li>Store and display your gratitude entries</li>
          <li>Enable sharing and collaboration on boards</li>
          <li>Send you important service-related emails (e.g., password reset)</li>
          <li>Ensure the security and integrity of our services</li>
        </ul>

        <p style={{ marginTop: "16px" }}><strong>We do NOT:</strong></p>
        <ul>
          <li>Sell your personal information to third parties</li>
          <li>Use your data for advertising or marketing purposes</li>
          <li>Track your activity across other apps or websites</li>
          <li>Share your data with third parties for their marketing purposes</li>
          <li>Use automated decision-making or profiling that affects you</li>
          <li>Use your data to train artificial intelligence or machine learning models</li>
        </ul>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>4. Data Sharing and Third-Party Services</h2>
        <p>We do not sell, trade, or otherwise transfer your personally identifiable information to outside parties. We use the following third-party services solely to operate the App:</p>

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "12px", marginBottom: "12px" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #ddd", textAlign: "left" }}>
              <th style={{ padding: "8px" }}>Service</th>
              <th style={{ padding: "8px" }}>Purpose</th>
              <th style={{ padding: "8px" }}>Data Shared</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: "1px solid #ddd" }}>
              <td style={{ padding: "8px" }}>Supabase</td>
              <td style={{ padding: "8px" }}>Authentication, database, file storage</td>
              <td style={{ padding: "8px" }}>Account data, content you create</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #ddd" }}>
              <td style={{ padding: "8px" }}>Apple</td>
              <td style={{ padding: "8px" }}>Sign in with Apple</td>
              <td style={{ padding: "8px" }}>Authentication tokens only</td>
            </tr>
            <tr style={{ borderBottom: "1px solid #ddd" }}>
              <td style={{ padding: "8px" }}>Google</td>
              <td style={{ padding: "8px" }}>Sign in with Google</td>
              <td style={{ padding: "8px" }}>Authentication tokens only</td>
            </tr>
          </tbody>
        </table>

        <p>These third-party services are bound by their own privacy policies which provide protection for your data.</p>

        <p style={{ marginTop: "12px" }}>
          We may disclose your information if required by law, court order, or governmental authority, or if we believe disclosure is necessary to protect our rights, your safety, or the safety of others.
        </p>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>5. Data Storage and Security</h2>
        <ul>
          <li>Your data is stored securely on Supabase's cloud infrastructure</li>
          <li>All data transmission uses HTTPS encryption</li>
          <li>Passwords are securely hashed and cannot be viewed by anyone, including us</li>
          <li>We use Supabase's built-in security features to protect your data</li>
        </ul>
        <p style={{ marginTop: "12px" }}>
          While we take reasonable measures to protect your data, no method of transmission or storage is 100% secure. If you discover a security vulnerability, please contact us immediately.
        </p>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>6. Data Retention and Deletion</h2>
        <p><strong>Retention:</strong> We retain your data for as long as your account is active or as needed to provide you services.</p>

        <p style={{ marginTop: "12px" }}><strong>Deletion:</strong> You can delete your account and all associated data directly within the App. When you delete your account:</p>
        <ul>
          <li>Your profile information will be permanently deleted</li>
          <li>Your gratitude entries will be permanently deleted</li>
          <li>Your data will be removed from our systems</li>
        </ul>

        <p style={{ marginTop: "12px" }}>
          We may retain certain information as required by law (e.g., for tax or legal compliance) or to resolve disputes.
        </p>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>7. Your Rights and Choices</h2>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>Access and Control</h3>
        <p>You have the right to:</p>
        <ul>
          <li>Access your personal data - view and edit your profile within the App</li>
          <li>Correct inaccurate data - edit your profile information at any time</li>
          <li>Delete individual gratitude entries at any time within the App</li>
          <li>Delete your entire account and all associated data within the App</li>
          <li>Request a copy of your data by contacting us</li>
        </ul>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>Email Privacy (Sign in with Apple)</h3>
        <p>
          If you sign in with Apple, you can choose to hide your email address. Apple will create a unique relay address that forwards to your real email, keeping your personal email private from us.
        </p>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>Revoking Social Login Access</h3>
        <p>You can revoke our access to your social login credentials:</p>
        <ul>
          <li><strong>Apple:</strong> Settings → [Your Name] → Password & Security → Apps Using Apple ID → Gratitude Board → Stop Using Apple ID</li>
          <li><strong>Google:</strong> Google Account → Security → Third-party apps with account access → Gratitude Board → Remove Access</li>
        </ul>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>8. International Users</h2>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>European Union (GDPR)</h3>
        <p>If you are in the European Economic Area, you have additional rights under GDPR:</p>
        <ul>
          <li><strong>Legal basis:</strong> We process your data based on your consent (account creation) and contract performance (providing the service)</li>
          <li><strong>Right to portability:</strong> Request your data in a machine-readable format by contacting us</li>
          <li><strong>Right to lodge a complaint:</strong> You may file a complaint with your local data protection authority</li>
        </ul>

        <h3 style={{ fontSize: "1.1em", marginTop: "16px" }}>California (CCPA)</h3>
        <p>If you are a California resident, you have the right to:</p>
        <ul>
          <li>Know what personal information we collect and how it's used</li>
          <li>Request deletion of your personal information</li>
          <li>Opt-out of the sale of personal information (we do not sell your data)</li>
          <li>Non-discrimination for exercising your privacy rights</li>
        </ul>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>9. Google API Services User Data Policy</h2>
        <p>
          Gratitude Board's use and transfer to any other app of information received from Google APIs will adhere to the{" "}
          <a href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes" target="_blank" rel="noreferrer">
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>10. Children's Privacy</h2>
        <p>
          Gratitude Board is not intended for children under 13 years of age (or under 16 in the European Economic Area). We do not knowingly collect personal information from children under these ages.
        </p>
        <p style={{ marginTop: "12px" }}>
          If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. If we discover we have collected personal information from a child under the applicable age, we will delete that information promptly.
        </p>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>11. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. When we make changes, we will update the "Last Updated" date at the top of this policy.
        </p>
        <p style={{ marginTop: "12px" }}>
          Your continued use of the App after changes constitutes acceptance of the updated policy.
        </p>
      </section>

      <section style={{ marginBottom: "24px" }}>
        <h2>12. Contact Us</h2>
        <p>If you have questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us:</p>
        <ul style={{ listStyle: "none", paddingLeft: 0 }}>
          <li><strong>Email:</strong> eddy.j.jiang@gmail.com</li>
        </ul>
        <p style={{ marginTop: "12px" }}>
          We will respond to your request within 30 days.
        </p>
      </section>
    </div>
  );
}
